import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { jobsAPI, customersAPI, employeesAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Repeat, Info, Paperclip, File, Download, Trash2, X, AlertTriangle } from 'lucide-react';
import { format, parseISO, addMonths } from 'date-fns';

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
];

export default function JobForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [employees, setEmployees] = useState([]);
  
  // File attachment state
  const [attachments, setAttachments] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const attachmentInputRef = useRef(null);
  
  // Scheduling conflict state
  const [conflicts, setConflicts] = useState([]);
  const [checkingConflicts, setCheckingConflicts] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    job_number: '',
    description: '',
    customer_id: '',
    assigned_to: [],
    start_time: '',
    end_time: '',
    status: 'pending',
    location: '',
    notes: '',
    cost_center: '',
    // Recurrence fields
    is_recurring: false,
    recurrence_pattern: 'weekly',
    recurrence_days: [],
    recurrence_end_date: '',
    recurrence_count: 12,
  });

  // Check for conflicts when employees or times change
  useEffect(() => {
    const checkConflicts = async () => {
      if (formData.assigned_to.length === 0 || !formData.start_time || !formData.end_time) {
        setConflicts([]);
        return;
      }
      
      setCheckingConflicts(true);
      try {
        const res = await jobsAPI.checkConflicts({
          employee_ids: formData.assigned_to,
          start_time: formData.start_time,
          end_time: formData.end_time,
          exclude_job_id: isEdit ? id : null
        });
        setConflicts(res.data.conflicts || []);
      } catch (error) {
        console.error('Failed to check conflicts:', error);
        setConflicts([]);
      } finally {
        setCheckingConflicts(false);
      }
    };
    
    // Debounce the conflict check
    const timeout = setTimeout(checkConflicts, 500);
    return () => clearTimeout(timeout);
  }, [formData.assigned_to, formData.start_time, formData.end_time, isEdit, id]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch all customers and employees for dropdown lists
        const [customersRes, employeesRes] = await Promise.all([
          customersAPI.getDropdownList(),
          employeesAPI.getAll({ page_size: 1000 }),
        ]);
        setCustomers(customersRes.data);
        setEmployees(employeesRes.data);

        if (isEdit) {
          const jobRes = await jobsAPI.get(id);
          const job = jobRes.data;
          setFormData({
            title: job.title || '',
            job_number: job.job_number || '',
            description: job.description || '',
            customer_id: job.customer_id || '',
            assigned_to: job.assigned_to || [],
            start_time: job.start_time ? format(parseISO(job.start_time), 'yyyy-MM-dd') : '',
            end_time: job.end_time ? format(parseISO(job.end_time), 'yyyy-MM-dd') : '',
            status: job.status || 'pending',
            location: job.location || '',
            notes: job.notes || '',
            cost_center: job.cost_center || '',
            is_recurring: job.is_recurring || false,
            recurrence_pattern: job.recurrence_pattern || 'weekly',
            recurrence_days: job.recurrence_days || [],
            recurrence_end_date: job.recurrence_end_date ? format(parseISO(job.recurrence_end_date), 'yyyy-MM-dd') : '',
            recurrence_count: 12,
          });
          
          // Load existing attachments
          try {
            const attachRes = await jobsAPI.getAttachments(id);
            setAttachments(attachRes.data || []);
          } catch (err) {
            console.error('Failed to load attachments:', err);
            setAttachments([]);
          }
        } else if (location.state?.date) {
          const date = format(location.state.date, 'yyyy-MM-dd');
          setFormData(prev => ({
            ...prev,
            start_time: date,
            end_time: date,
            recurrence_end_date: format(addMonths(new Date(date), 3), 'yyyy-MM-dd'),
          }));
        } else {
          // Set default end date to 3 months from now
          setFormData(prev => ({
            ...prev,
            recurrence_end_date: format(addMonths(new Date(), 3), 'yyyy-MM-dd'),
          }));
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
        toast.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, isEdit, location.state]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleEmployeeToggle = (employeeId) => {
    setFormData(prev => ({
      ...prev,
      assigned_to: prev.assigned_to.includes(employeeId)
        ? prev.assigned_to.filter(id => id !== employeeId)
        : [...prev.assigned_to, employeeId],
    }));
  };

  const handleDayToggle = (dayValue) => {
    setFormData(prev => ({
      ...prev,
      recurrence_days: prev.recurrence_days.includes(dayValue)
        ? prev.recurrence_days.filter(d => d !== dayValue)
        : [...prev.recurrence_days, dayValue].sort((a, b) => a - b),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.start_time || !formData.end_time) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate recurring settings
    if (formData.is_recurring && formData.recurrence_pattern === 'weekly' && formData.recurrence_days.length === 0) {
      toast.error('Please select at least one day for weekly recurrence');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        customer_id: formData.customer_id || null,
        // Date-only input: pin start to 00:00 local and end to 23:59 local
        start_time: new Date(`${formData.start_time}T00:00:00`).toISOString(),
        end_time: new Date(`${formData.end_time}T23:59:59`).toISOString(),
        recurrence_end_date: formData.is_recurring && formData.recurrence_end_date 
          ? new Date(formData.recurrence_end_date).toISOString() 
          : null,
        recurrence_days: formData.is_recurring ? formData.recurrence_days : null,
        recurrence_pattern: formData.is_recurring ? formData.recurrence_pattern : null,
        recurrence_count: formData.is_recurring ? formData.recurrence_count : null,
      };

      if (isEdit) {
        await jobsAPI.update(id, payload);
        
        // Upload pending files
        if (pendingFiles.length > 0) {
          setUploadingFiles(true);
          let uploadedCount = 0;
          for (const file of pendingFiles) {
            try {
              await jobsAPI.uploadAttachment(id, file);
              uploadedCount++;
            } catch (err) {
              console.error('Failed to upload file:', file.name, err);
            }
          }
          if (uploadedCount > 0) {
            toast.success(`${uploadedCount} file(s) attached`);
          }
          setUploadingFiles(false);
        }
        
        toast.success('Job updated successfully');
      } else {
        const res = await jobsAPI.create(payload);
        const newJobId = res.data?.id;
        
        // Upload pending files for new job
        if (pendingFiles.length > 0 && newJobId) {
          setUploadingFiles(true);
          let uploadedCount = 0;
          for (const file of pendingFiles) {
            try {
              await jobsAPI.uploadAttachment(newJobId, file);
              uploadedCount++;
            } catch (err) {
              console.error('Failed to upload file:', file.name, err);
            }
          }
          if (uploadedCount > 0) {
            toast.success(`${uploadedCount} file(s) attached`);
          }
          setUploadingFiles(false);
        }
        
        if (formData.is_recurring) {
          toast.success(`Recurring job created! Multiple instances have been scheduled.`);
        } else {
          toast.success('Job created successfully');
        }
      }
      navigate('/jobs');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save job');
    } finally {
      setSaving(false);
    }
  };

  // File attachment handlers
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setPendingFiles(prev => [...prev, ...files]);
    if (attachmentInputRef.current) {
      attachmentInputRef.current.value = '';
    }
  };

  const handleRemovePendingFile = (index) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!isEdit) return;
    try {
      await jobsAPI.deleteAttachment(id, attachmentId);
      setAttachments(prev => prev.filter(a => a.id !== attachmentId));
      toast.success('Attachment deleted');
    } catch (error) {
      toast.error('Failed to delete attachment');
    }
  };

  const handleDownloadAttachment = async (attachment) => {
    if (!isEdit) return;
    try {
      const blob = await jobsAPI.downloadAttachment(id, attachment.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download attachment');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in max-w-3xl" data-testid="job-form">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="font-heading text-3xl font-bold">
            {isEdit ? 'Edit Job' : 'New Job'}
          </h1>
          <p className="text-muted-foreground mt-1">
            {isEdit ? 'Update job details' : 'Create a new job assignment'}
          </p>
        </div>
      </div>

      {/* Form */}
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="title">Job Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                placeholder="Enter job title"
                required
                data-testid="job-title-input"
              />
            </div>

            {/* Job Number */}
            <div className="space-y-2">
              <Label htmlFor="job_number">Job Number</Label>
              <div className="flex gap-2">
                <Input
                  id="job_number"
                  value={formData.job_number}
                  onChange={(e) => handleChange('job_number', e.target.value)}
                  placeholder="Enter a custom number or generate one"
                  className="font-mono"
                  data-testid="job-number-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={async () => {
                    try {
                      const res = await jobsAPI.generateNumber();
                      handleChange('job_number', res.data.job_number);
                    } catch (error) {
                      toast.error('Failed to generate job number');
                    }
                  }}
                  data-testid="generate-job-number-btn"
                >
                  Generate
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Leave blank to auto-assign a number (format: J-YYYYMMDD-001). Custom numbers must be unique.
              </p>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Enter job description"
                rows={3}
                data-testid="job-description-input"
              />
            </div>

            {/* Customer & Status */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Customer</Label>
                <Select
                  value={formData.customer_id || "none"}
                  onValueChange={(value) => handleChange('customer_id', value === "none" ? "" : value)}
                >
                  <SelectTrigger data-testid="customer-select">
                    <SelectValue placeholder="Select customer" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No customer</SelectItem>
                    {customers.map((customer) => (
                      <SelectItem key={customer.id} value={customer.id}>
                        {customer.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => handleChange('status', value)}
                >
                  <SelectTrigger data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Date */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start_time">Start Date *</Label>
                <Input
                  id="start_time"
                  type="date"
                  value={formData.start_time}
                  onChange={(e) => handleChange('start_time', e.target.value)}
                  required
                  data-testid="start-time-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="end_time">End Date *</Label>
                <Input
                  id="end_time"
                  type="date"
                  value={formData.end_time}
                  onChange={(e) => handleChange('end_time', e.target.value)}
                  required
                  data-testid="end-time-input"
                />
              </div>
            </div>

            {/* Location */}
            <div className="space-y-2">
              <Label htmlFor="location">Location</Label>
              <Input
                id="location"
                value={formData.location}
                onChange={(e) => handleChange('location', e.target.value)}
                placeholder="Enter job location"
                data-testid="location-input"
              />
            </div>

            {/* Cost Center */}
            <div className="space-y-2">
              <Label htmlFor="cost_center">Division</Label>
              <Input
                id="cost_center"
                value={formData.cost_center}
                onChange={(e) => handleChange('cost_center', e.target.value)}
                placeholder="Enter division"
                data-testid="cost-center-input"
              />
            </div>

            {/* Recurring Job Section */}
            {!isEdit && (
              <div className="border rounded-lg p-4 space-y-4 bg-muted/30">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Repeat className="w-5 h-5 text-blue-600" />
                    <Label htmlFor="is_recurring" className="font-medium">Recurring Job</Label>
                  </div>
                  <Switch
                    id="is_recurring"
                    checked={formData.is_recurring}
                    onCheckedChange={(checked) => handleChange('is_recurring', checked)}
                    data-testid="recurring-switch"
                  />
                </div>

                {formData.is_recurring && (
                  <div className="space-y-4 pt-2 border-t">
                    {/* Recurrence Pattern */}
                    <div className="space-y-2">
                      <Label>Repeat</Label>
                      <Select
                        value={formData.recurrence_pattern}
                        onValueChange={(value) => handleChange('recurrence_pattern', value)}
                      >
                        <SelectTrigger data-testid="recurrence-pattern">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="daily">Daily</SelectItem>
                          <SelectItem value="weekly">Weekly</SelectItem>
                          <SelectItem value="biweekly">Bi-weekly</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Days of Week (for weekly/biweekly) */}
                    {(formData.recurrence_pattern === 'weekly' || formData.recurrence_pattern === 'biweekly') && (
                      <div className="space-y-2">
                        <Label>Repeat on</Label>
                        <div className="flex flex-wrap gap-2">
                          {DAYS_OF_WEEK.map((day) => (
                            <button
                              key={day.value}
                              type="button"
                              onClick={() => handleDayToggle(day.value)}
                              className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
                                formData.recurrence_days.includes(day.value)
                                  ? 'bg-primary text-primary-foreground border-primary'
                                  : 'bg-background hover:bg-muted border-input'
                              }`}
                              data-testid={`day-${day.label.toLowerCase()}`}
                            >
                              {day.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* End Date & Count */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="recurrence_end_date">End Date</Label>
                        <Input
                          id="recurrence_end_date"
                          type="date"
                          value={formData.recurrence_end_date}
                          onChange={(e) => handleChange('recurrence_end_date', e.target.value)}
                          data-testid="recurrence-end-date"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="recurrence_count">Max Occurrences</Label>
                        <Input
                          id="recurrence_count"
                          type="number"
                          min="1"
                          max="52"
                          value={formData.recurrence_count}
                          onChange={(e) => handleChange('recurrence_count', parseInt(e.target.value) || 12)}
                          data-testid="recurrence-count"
                        />
                      </div>
                    </div>

                    {/* Info */}
                    <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-sm text-blue-700 dark:text-blue-300">
                      <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <p>
                        This will create multiple job instances based on your schedule. 
                        Each instance can be edited individually after creation.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Show info if editing a recurring instance */}
            {isEdit && formData.is_recurring && (
              <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-sm text-blue-700 dark:text-blue-300">
                <Repeat className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <p>
                  This is part of a recurring series. Changes here will only affect this instance.
                </p>
              </div>
            )}

            {/* Assign Employees */}
            <div className="space-y-3">
              <Label>Assign Employees</Label>
              <div className="border rounded-lg p-4 max-h-48 overflow-y-auto space-y-2">
                {employees.length === 0 ? (
                  <p className="text-muted-foreground text-sm">No employees available</p>
                ) : (
                  employees.map((employee) => (
                    <div key={employee.id} className="flex items-center gap-3">
                      <Checkbox
                        id={`employee-${employee.id}`}
                        checked={formData.assigned_to.includes(employee.id)}
                        onCheckedChange={() => handleEmployeeToggle(employee.id)}
                        data-testid={`employee-checkbox-${employee.id}`}
                      />
                      <label
                        htmlFor={`employee-${employee.id}`}
                        className="text-sm font-medium cursor-pointer"
                      >
                        {employee.name}
                        <span className="text-muted-foreground ml-2">({employee.role})</span>
                      </label>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Scheduling Conflict Alert */}
            {conflicts.length > 0 && (
              <Alert variant="destructive" className="border-orange-500 bg-orange-50 dark:bg-orange-900/20">
                <AlertTriangle className="h-4 w-4 text-orange-600" />
                <AlertTitle className="text-orange-700 dark:text-orange-400">Scheduling Conflict Detected</AlertTitle>
                <AlertDescription className="text-orange-600 dark:text-orange-300">
                  <p className="mb-2">The following employees have overlapping jobs:</p>
                  <ul className="space-y-2 text-sm">
                    {conflicts.map((conflict) => (
                      <li key={conflict.employee_id} className="border-l-2 border-orange-400 pl-2">
                        <span className="font-medium">{conflict.employee_name}</span>
                        <ul className="mt-1 space-y-1 text-xs">
                          {conflict.conflicting_jobs.map((job) => (
                            <li key={job.job_id}>
                              • {job.job_title} ({format(parseISO(job.start_time), 'MMM d, h:mm a')} - {format(parseISO(job.end_time), 'h:mm a')})
                            </li>
                          ))}
                        </ul>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-2 text-xs italic">You can still save, but the employee will be double-booked.</p>
                </AlertDescription>
              </Alert>
            )}

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                placeholder="Additional notes"
                rows={2}
                data-testid="notes-input"
              />
            </div>

            {/* File Attachments Section */}
            <div className="space-y-2">
              <Label>Attachments</Label>
              <div className="border rounded-lg p-3 space-y-3">
                {/* Existing attachments (when editing) */}
                {attachments.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground font-medium">Existing Files</p>
                    {attachments.map((attachment) => (
                      <div
                        key={attachment.id}
                        className="flex items-center justify-between p-2 bg-muted/50 rounded-md"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <File className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                          <span className="text-sm truncate">{attachment.filename}</span>
                          <span className="text-xs text-muted-foreground">
                            ({formatFileSize(attachment.size)})
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownloadAttachment(attachment)}
                            className="h-7 w-7 p-0"
                          >
                            <Download className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteAttachment(attachment.id)}
                            className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Pending files (to be uploaded) */}
                {pendingFiles.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground font-medium">New Files (will be uploaded on save)</p>
                    {pendingFiles.map((file, index) => (
                      <div
                        key={`${file.name}-${file.size}-${file.lastModified}`}
                        className="flex items-center justify-between p-2 bg-blue-50 dark:bg-blue-900/20 rounded-md"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <Paperclip className="w-4 h-4 text-blue-500 flex-shrink-0" />
                          <span className="text-sm truncate">{file.name}</span>
                          <span className="text-xs text-muted-foreground">
                            ({formatFileSize(file.size)})
                          </span>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemovePendingFile(index)}
                          className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                        >
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Add file button */}
                <div>
                  <input
                    ref={attachmentInputRef}
                    type="file"
                    multiple
                    className="hidden"
                    onChange={handleFileSelect}
                    data-testid="job-attachment-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => attachmentInputRef.current?.click()}
                    className="w-full"
                  >
                    <Paperclip className="w-4 h-4 mr-2" />
                    Add Files
                  </Button>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button type="submit" className="flex-1" disabled={saving || uploadingFiles} data-testid="save-job-btn">
                {saving || uploadingFiles ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {uploadingFiles ? 'Uploading...' : 'Saving...'}
                  </>
                ) : isEdit ? (
                  'Update Job'
                ) : formData.is_recurring ? (
                  'Create Recurring Job'
                ) : (
                  'Create Job'
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
